(() => {
  if (customElements.get('fls-delivery-estimate')) return;

  const chileClock = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  });
  // Dates below are calendar dates stored at UTC midnight, not instants in Chile.
  const dayMonth = new Intl.DateTimeFormat('es-CL', { timeZone: 'UTC', day: 'numeric', month: 'short' });
  const weekday = new Intl.DateTimeFormat('es-CL', { timeZone: 'UTC', weekday: 'long' });
  const shortWeekday = new Intl.DateTimeFormat('es-CL', { timeZone: 'UTC', weekday: 'short' });
  const iso = (date) => date.toISOString().slice(0, 10);
  const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
  const formatDate = (date) => dayMonth.format(date).replace(/\./g, '');
  const formatWeekday = (date) => shortWeekday.format(date).replace(/\./g, '');
  const integer = (value, fallback, min, max) => {
    const number = value === '' || value == null ? fallback : Number(value);
    return Math.min(max, Math.max(min, Number.isFinite(number) ? Math.floor(number) : fallback));
  };

  class DeliveryEstimate extends HTMLElement {
    static calculate(now, config = {}) {
      const parts = Object.fromEntries(chileClock.formatToParts(now).map(({ type, value }) => [type, value]));
      const today = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
      const cutoff = integer(config.cutoffHour, 14, 0, 23);
      const min = integer(config.minDays, 1, 1, 7);
      const max = Math.max(min, integer(config.maxDays, 2, 1, 14));
      const extra = integer(config.afterCutoffDays, 1, 0, 3);
      const closed = new Set(String(config.closedDates || '').match(/\b\d{4}-\d{2}-\d{2}\b/g) || []);
      const isWorkingDay = (date) => date.getUTCDay() !== 0 && date.getUTCDay() !== 6 && !closed.has(iso(date));
      const addWorkingDays = (date, count) => {
        const result = new Date(date);
        while (count > 0) {
          result.setUTCDate(result.getUTCDate() + 1);
          if (isWorkingDay(result)) count--;
        }
        return result;
      };
      const workingToday = isWorkingDay(today);
      const beforeCutoff = workingToday && Number(parts.hour) < cutoff;
      // Weekends always use the next business-day range, without the cutoff delay.
      const delay = workingToday && !beforeCutoff ? extra : 0;
      return {
        today, cutoff, beforeCutoff, workingToday,
        weekend: today.getUTCDay() === 0 || today.getUTCDay() === 6,
        minutesLeft: beforeCutoff ? Math.ceil((cutoff * 3600 - Number(parts.hour) * 3600 - Number(parts.minute) * 60 - Number(parts.second)) / 60) : 0,
        dispatch: beforeCutoff ? today : addWorkingDays(today, 1),
        earliest: addWorkingDays(today, min + delay),
        latest: addWorkingDays(today, max + delay),
      };
    }

    connectedCallback() {
      this.available = this.dataset.available === 'true';
      this.onVisibility = () => { if (!document.hidden) this.refresh(); };
      document.addEventListener('visibilitychange', this.onVisibility);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.sectionId) return;
          this.available = Boolean(data.variant?.available);
          this.refresh();
        });
      }
      this.refresh();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.unsubscribe?.();
    }

    refresh() {
      clearTimeout(this.timer);
      const estimate = DeliveryEstimate.calculate(new Date(), this.dataset);
      const { today, dispatch, earliest, latest } = estimate;
      const set = (name, text) => { this.querySelector(`[data-delivery-${name}]`).textContent = text; };
      const tomorrow = new Date(today);
      tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
      const dispatchLabel = iso(dispatch) === iso(today) ? 'Hoy'
        : iso(dispatch) === iso(tomorrow) ? 'Mañana' : capitalize(weekday.format(dispatch));

      set('title', estimate.beforeCutoff ? `Compra antes de las ${estimate.cutoff}:00`
        : estimate.weekend ? 'Compra este fin de semana' : 'Compra hoy');
      set('message', estimate.beforeCutoff ? 'Te quedan '
        : iso(dispatch) === iso(tomorrow) ? 'Despachamos mañana' : `Despachamos el ${weekday.format(dispatch)}`);
      const countdown = this.querySelector('[data-delivery-countdown]');
      countdown.hidden = !estimate.beforeCutoff;
      const hours = Math.floor(estimate.minutesLeft / 60);
      const minutes = estimate.minutesLeft % 60;
      countdown.textContent = hours ? `${hours} h ${String(minutes).padStart(2, '0')} min` : `${minutes} min`;

      this.querySelector('[data-delivery-order]').dateTime = iso(today);
      set('order-detail', formatDate(today));
      this.querySelector('[data-delivery-dispatch]').dateTime = iso(dispatch);
      set('dispatch', dispatchLabel);
      set('dispatch-detail', formatDate(dispatch));
      const sameDate = iso(earliest) === iso(latest);
      const sameMonth = earliest.getUTCMonth() === latest.getUTCMonth() && earliest.getUTCFullYear() === latest.getUTCFullYear();
      set('range', sameDate ? formatDate(earliest) : sameMonth
        ? `${earliest.getUTCDate()}–${formatDate(latest)}` : `${formatDate(earliest)} – ${formatDate(latest)}`);
      set('weekdays', sameDate ? capitalize(weekday.format(earliest))
        : `${capitalize(formatWeekday(earliest))} – ${formatWeekday(latest)}`);
      const range = this.querySelector('[data-delivery-range]');
      range.setAttribute('aria-label', `Entrega estimada ${sameDate ? 'el' : 'entre el'} ${formatDate(earliest)}${sameDate ? '' : ` y el ${formatDate(latest)}`}`);
      this.hidden = !this.available;
      this.timer = setTimeout(() => this.refresh(), 60000 - (Date.now() % 60000) + 50);
    }
  }

  customElements.define('fls-delivery-estimate', DeliveryEstimate);
})();
